import { ExpandMore } from '@mui/icons-material';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { ReactNode, useState } from 'react';

interface AccordionProps {
  title: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

// Thin wrapper around MUI's Accordion, flattened to match this app's
// bordered-list visual language (soft slate border, light radius) instead
// of MUI's default paper elevation/shadow.
const Accordion = ({
  title,
  defaultOpen = false,
  children,
}: AccordionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const [prevDefaultOpen, setPrevDefaultOpen] = useState(defaultOpen);

  // Callers often derive defaultOpen from data that loads after mount, so
  // open on its false -> true transition. Never auto-close: a user's toggle
  // and a later drop to false are left alone.
  if (defaultOpen !== prevDefaultOpen) {
    setPrevDefaultOpen(defaultOpen);
    if (defaultOpen) setOpen(true);
  }

  return (
    <MuiAccordion
      expanded={open}
      onChange={(_event, isExpanded) => setOpen(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid rgba(15, 23, 42, 0.1)',
        borderRadius: '0.5rem',
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0 },
        '& + &': { marginTop: '0.75rem' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ fontSize: '1.25rem' }} />}
        sx={{
          minHeight: '2.75rem',
          px: '1rem',
          '& .MuiAccordionSummary-content': {
            minWidth: 0,
            my: '0.75rem',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: '#94A3B8',
          },
          '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
            color: 'var(--primary-color)',
          },
        }}
      >
        {title}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          borderTop: '1px solid rgba(15, 23, 42, 0.08)',
          px: '1rem',
          pt: '0.875rem',
          pb: '1rem',
        }}
      >
        {children}
      </AccordionDetails>
    </MuiAccordion>
  );
};

export default Accordion;
